import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { NegotiationsGridTemplate as template } from './negotiations-grid.template';
import { NegotationsGridStyles as styles } from './negotations-grid.styles';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { IEnumService } from '../../../services/enum.service';
import { NEGOTIATION_STATUS, PROFILE_RIGHTS, ProfileVisibility } from '../../../utils';
import { INegotiationService } from '../../../services';
import { Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { updateArray } from '../../../utils/dataserver';
import { CustomNoRowsOverlay } from '../../../utils/custom-renderer/custom-no-rows-overlay';
import { IUserService } from '../../../services/user.service';
import { ICompanyService } from '../../../services/company.service';
import { GridDelegate } from '../../../utils/grid-delegate';
import { COMPANY_ROLE } from '../../mf/companys/edit';
import { shouldAutosizeByStateKey } from '../../../utils/grid';

export enum SHARED_DASHBOARD_GRID_TABS {
  NEGOTIATIONS_TAB = 'negotiations',
  EXECUTED_TAB = 'executed',
  TERMINATED_TAB = 'terminated',
}

export enum SHARED_DASHBOARD_GRID_PANELS {
  NEGOTIATIONS_PANEL = 'negotiationsPanel',
  EXECUTED_PANEL = 'executedPanel',
  TERMINATED_PANEL = 'terminatedPanel',
}

enum STATE_KEYS {
  NEGOTIATION = 'dashboard-negotiation-grid',
  EXECUTED = 'dashboard-executed-grid',
  TERMINATED = 'dashboard-terminated-grid',
}

const name = 'negotiation-seller-dashboard';

@customElement({
  name,
  template,
  styles,
})
export class NegotiationsGrid extends FASTElement {
  @ICompanyService companyService: ICompanyService;
  @INegotiationService negotiationService: INegotiationService;
  @IEnumService enumService: IEnumService;
  @IUserService userService: IUserService;

  @observable negotiationsGrid: zeroAgGrid;
  @observable matchCriteria: string;

  @observable negotiationSubscription: SocketSubscription;

  @observable allNegotiations: any[];

  @attr projectId: string;
  @attr @observable activeTabId: SHARED_DASHBOARD_GRID_TABS;
  @attr projectListEmpty: boolean;
  private companyCriteria: string = '';
  @observable currentStateKey: STATE_KEYS;

  private gridDelegate: GridDelegate;

  private negotiationsTabs: SHARED_DASHBOARD_GRID_TABS[] = [
    SHARED_DASHBOARD_GRID_TABS.NEGOTIATIONS_TAB,
    SHARED_DASHBOARD_GRID_TABS.EXECUTED_TAB,
    SHARED_DASHBOARD_GRID_TABS.TERMINATED_TAB,
  ];

  connectedCallback() {
    super.connectedCallback();

    if (this.companyService.getCurrentCompany().COMPANY_ROLE === COMPANY_ROLE.CASCADE_ADMIN) {
      this.companyCriteria = '';
    } else if (
      this.userService.hasBuyerAndSellerVisibility() &&
      this.userService.getCurrentSide() === ProfileVisibility.SELLER
    ) {
      this.companyCriteria = `SELLER_COMPANY_ID == '${
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }'`;
    } else if (
      this.userService.hasBuyerAndSellerVisibility() &&
      this.userService.getCurrentSide() === ProfileVisibility.BUYER
    ) {
      this.companyCriteria = `BUYER_COMPANY_ID == '${
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }'`;
    }

    this.matchCriteria =
      `(NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.IN_PROGRESS_ARTERIA}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.PENDING_EXECUTION}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.PENDING_EXECUTION_TIMER}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.READY_FOR_TRADE_EXECUTION}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.REVIEW_EXECUTION_CHECKLIST}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.EXECUTED_AWAITING_BUYER}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.EXECUTED_AWAITING_SELLER}' ` +
      `|| NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.EXPIRED}')`;

    this.updateGridOptions();
  }

  public projectListEmptyChanged() {
    if (this.negotiationsGrid && this.negotiationsGrid['agGridOptions']) {
      this.negotiationsGrid['agGridOptions']['noRowsOverlayComponentParams'] = {
        callback: () => {
          if (!this.projectListEmpty) {
            this.openVppaModal();
          }
        },
        innerText: !this.projectListEmpty ? 'Add New Contract' : 'No Projects or Trades listed',
        clickable: !this.projectListEmpty ? 'true' : 'false',
      };
    }
  }

  private updateGridOptions() {
    this.negotiationsGrid.gridOptions = {
      tooltipShowDelay: 300,
      onRowDoubleClicked: (row) => this.rowDoubleClicked(row),
      onFirstDataRendered: () => this.firstDataRendered(),
      suppressCellFocus: true,
      suppressBrowserResizeObserver: true,
      scrollbarWidth: 10,
      animateRows: true,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
      rowClass: 'transparent-background',
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivotMode: true,
            },
          },
        ],
      },
    };

    if (this.hasPermissionsToCreateContract()) {
      // Add additional options if they have permission
      this.negotiationsGrid.gridOptions = {
        noRowsOverlayComponent: CustomNoRowsOverlay,
        noRowsOverlayComponentParams: {
          callback: () => {
            if (!this.projectListEmpty) {
              this.openVppaModal();
            }
          },
          innerText: !this.projectListEmpty ? 'Add New Contract' : 'No Projects or Trades listed',
          clickable: !this.projectListEmpty ? 'true' : 'false',
        },
        ...this.negotiationsGrid.gridOptions,
      };
    }

    this.loadNegotiations(this.matchCriteria, this.projectId);
  }

  rowDoubleClicked(row) {
    if (this.activeTabId === SHARED_DASHBOARD_GRID_TABS.EXECUTED_TAB)
      this.negotiationService.openNegotiationDetailModal(row.data?.NEGOTIATION_ID);
  }

  public openVppaModal() {
    this.$emit('openModal');
  }

  public hasPermissionsToCreateContract(): boolean {
    return this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.UPSERT_VPPA);
  }

  private loadNegotiations(negotiationCriteria, projectID?) {
    this.negotiationsGrid?.gridApi?.setRowData([]);
    let matchCriteria = `${this.companyCriteria} `;
    if (!negotiationCriteria) {
      matchCriteria = `${this.companyCriteria ? ' && ' : ''} (NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.IN_PROGRESS
      }' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.REQUIRES_CONFIRMATION
      }' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.CONFIRMED_AWAITING_BUYER
      }' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.CONFIRMED_AWAITING_SELLER
      }' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.SUCCESSFUL}' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.REVIEW_DRAFT
      }' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.IN_PROGRESS_ARTERIA
      }' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.REVIEW_EXECUTION_CHECKLIST
      }' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.READY_FOR_TRADE_EXECUTION
      }' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.PENDING_EXECUTION
      }' || NEGOTIATION_STATUS == '${
        NEGOTIATION_STATUS.EXECUTED_AWAITING_BUYER
      }' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.EXECUTED_AWAITING_SELLER}') && `;
    } else {
      matchCriteria = matchCriteria.concat(
        `${this.companyCriteria ? ' && ' : ''} ${negotiationCriteria}`,
      );
    }
    if (projectID) {
      matchCriteria = matchCriteria.concat(` &&  PROJECT_ID == '${projectID}'`);
    }

    this.allNegotiations = [];
    this.negotiationSubscription?.unsubscribe();
    this.negotiationSubscription = this.negotiationService
      .getNegotiationDashboardData({
        CRITERIA_MATCH: matchCriteria,
      })
      .subscribe({
        next: (streamMessage: Message) => {
          const resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.allNegotiations = updateArray(resultList, this.allNegotiations);
          } else {
            this.allNegotiations = [];
          }
          this.negotiationsGrid?.gridApi?.setRowData(this.allNegotiations);
        },
        error: (error: any) => {
          this.allNegotiations = [];
        },
      });
  }

  matchCriteriaChanged(oldValue, newValue) {
    this.loadNegotiations(newValue, this.projectId);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.negotiationSubscription.unsubscribe();
  }

  public onGridReady() {
    this.negotiationsGrid.gridApi.setRowData(this.allNegotiations);
    this.gridDelegate = new GridDelegate(this.negotiationsGrid, this.currentStateKey);
  }

  projectIdChanged(oldValue, newValue) {
    this.loadNegotiations(this.matchCriteria, newValue);
  }

  public activeTabIdChanged() {
    this.getStateKey();
  }

  private getStateKey() {
    switch (this.activeTabId) {
      case SHARED_DASHBOARD_GRID_TABS.NEGOTIATIONS_TAB:
        this.currentStateKey = STATE_KEYS.NEGOTIATION;
        break;
      case SHARED_DASHBOARD_GRID_TABS.EXECUTED_TAB:
        this.currentStateKey = STATE_KEYS.EXECUTED;
        break;
      case SHARED_DASHBOARD_GRID_TABS.TERMINATED_TAB:
        this.currentStateKey = STATE_KEYS.TERMINATED;
        break;
    }
    this.gridDelegate?.changeState(this.currentStateKey, true);
    this.checkIfShouldAutosize();
  }

  private firstDataRendered() {
    // Check on first render of data (If you check on grid ready, the columns will be squished because there is no data yet)
    this.checkIfShouldAutosize();
  }

  private checkIfShouldAutosize() {
    if (this.negotiationsTabs.includes(this.activeTabId)) {
      const shouldAutosize = shouldAutosizeByStateKey(this.currentStateKey);
      if (shouldAutosize) {
        this.negotiationsGrid?.columnApi.autoSizeAllColumns();
      }
    }
  }
}
